import { ArrowRight, Printer, Sparkles } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useShop } from '../../../shared/store';
import SafeImage from '../../../shared/components/SafeImage';
import { podBooksService, type PodBookListing } from '../../../shared/services';
import { paymentService } from '../../../shared/services/paymentService';
import { convertRwfToUsd, formatRwf, formatUsd } from '../../../shared/utils/currency';

const PrintOnDemandPage = () => {
  const { themeMode } = useShop();
  const isDark = themeMode === 'dark';
  const [podBooks, setPodBooks] = useState<PodBookListing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [usdToRwfRate, setUsdToRwfRate] = useState<number | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadPodBooks = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const data = await podBooksService.getPodBooks();
        if (!isMounted) {
          return;
        }
        setPodBooks(data);
      } catch (error: unknown) {
        if (!isMounted) {
          return;
        }
        setLoadError(error instanceof Error ? error.message : 'Unable to load POD books right now.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadPodBooks();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rate = await paymentService.getCurrencyRate();
        if (!cancelled) setUsdToRwfRate(rate);
      } catch {
        // Optional - keep RWF-only display if config is missing.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const visiblePodBooks = useMemo(() => {
    const publishedBooks = podBooks.filter(
      (book) => book.approvedByAdmin || book.isPhysicalAvailable || book.podStatus === 'published',
    );

    return publishedBooks.length > 0 ? publishedBooks : podBooks;
  }, [podBooks]);

  const featuredBooks = useMemo(() => visiblePodBooks.slice(0, 3), [visiblePodBooks]);
  const publishedCount = useMemo(
    () => visiblePodBooks.filter((book) => book.approvedByAdmin || book.podStatus === 'published').length,
    [visiblePodBooks],
  );
  const formatCount = useMemo(
    () => new Set(visiblePodBooks.map((book) => book.size).filter(Boolean)).size || 1,
    [visiblePodBooks],
  );

  return (
    <section className={`relative overflow-hidden ${isDark ? 'bg-[#080606]' : 'bg-[#fdf6ef]'}`}>
      <div
        className={`absolute inset-0 ${
          isDark
            ? 'bg-[radial-gradient(circle_at_14%_18%,rgba(249,115,22,0.24),transparent_26%),radial-gradient(circle_at_84%_16%,rgba(56,189,248,0.16),transparent_24%),linear-gradient(90deg,rgba(8,6,6,0.98)_0%,rgba(20,10,7,0.96)_42%,rgba(10,16,24,0.98)_100%)]'
            : 'bg-[radial-gradient(circle_at_14%_18%,rgba(255,180,96,0.26),transparent_26%),radial-gradient(circle_at_84%_16%,rgba(110,220,255,0.2),transparent_24%),linear-gradient(90deg,rgba(255,237,214,0.98)_0%,rgba(255,247,238,0.96)_42%,rgba(234,248,255,0.98)_100%)]'
        }`}
      />
      <div
        className={`absolute inset-0 [background-image:radial-gradient(rgba(148,163,184,0.22)_1px,transparent_1px)] [background-size:18px_18px] ${
          isDark ? 'opacity-35' : 'opacity-55'
        }`}
      />
      <div className={`pointer-events-none absolute -left-16 top-16 h-56 w-56 rounded-full blur-3xl ${isDark ? 'bg-orange-500/18' : 'bg-orange-300/30'}`} />
      <div className={`pointer-events-none absolute right-0 top-10 h-72 w-72 rounded-full blur-3xl ${isDark ? 'bg-sky-400/18' : 'bg-sky-200/35'}`} />

      <div className="relative pb-20 pt-10 sm:pt-12">
        <section className={`relative overflow-hidden border-y bg-transparent ${isDark ? 'border-white/10' : 'border-orange-100/70'}`}>
          <style>{`
            @keyframes pod-showcase-float {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-10px); }
            }
            @keyframes pod-showcase-glow {
              0%, 100% { opacity: 0.55; transform: scale(1); }
              50% { opacity: 0.92; transform: scale(1.05); }
            }
          `}</style>

          <div
            className={`absolute inset-0 ${
              isDark
                ? 'bg-[radial-gradient(circle_at_18%_12%,rgba(249,115,22,0.18),transparent_24%),radial-gradient(circle_at_88%_20%,rgba(255,255,255,0.08),transparent_24%),linear-gradient(90deg,rgba(40,18,10,0.35)_0%,rgba(255,255,255,0.03)_48%,rgba(16,32,46,0.28)_100%)]'
                : 'bg-[radial-gradient(circle_at_18%_12%,rgba(255,194,120,0.18),transparent_24%),radial-gradient(circle_at_88%_20%,rgba(255,255,255,0.35),transparent_24%),linear-gradient(90deg,rgba(255,239,221,0.45)_0%,rgba(255,255,255,0.08)_48%,rgba(220,244,255,0.38)_100%)]'
            }`}
          />
          <div
            className={`pointer-events-none absolute -left-10 top-8 h-44 w-44 rounded-full blur-3xl ${isDark ? 'bg-orange-500/18' : 'bg-orange-400/20'}`}
            style={{ animation: 'pod-showcase-glow 5.2s ease-in-out infinite' }}
          />
          <div
            className={`pointer-events-none absolute right-8 top-10 h-56 w-56 rounded-full blur-3xl ${isDark ? 'bg-sky-400/16' : 'bg-sky-200/30'}`}
            style={{ animation: 'pod-showcase-glow 5.8s ease-in-out infinite' }}
          />

          <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-10 sm:px-6 sm:py-12 lg:grid-cols-[1fr_0.95fr] lg:items-center lg:px-8 lg:py-16">
            <div
              className={`max-w-2xl rounded-[32px] border p-6 shadow-[0_26px_70px_-50px_rgba(15,23,42,0.22)] backdrop-blur-xl sm:p-7 ${
                isDark ? 'border-white/10 bg-[#120d0b]/78 shadow-[0_26px_70px_-50px_rgba(0,0,0,0.65)]' : 'border-white/70 bg-white/72'
              }`}
            >
              <div
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] shadow-sm backdrop-blur ${
                  isDark ? 'border-white/10 bg-white/5 text-slate-200' : 'border-orange-200 bg-white/88 text-slate-700'
                }`}
              >
                <Sparkles className="h-4 w-4 text-orange-500" />
                Haapa POD
              </div>

              <p className={`mt-6 text-sm font-semibold uppercase tracking-[0.22em] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Printable books marketplace
              </p>
              <h1 className={`mt-2 text-5xl font-black uppercase leading-[0.88] sm:text-6xl xl:text-7xl ${isDark ? 'text-white' : 'text-slate-950'}`}>
                Print
                <br />
                Stories
                <br />
                <span className="text-orange-500">That Sell</span>
              </h1>
              <p className={`mt-5 max-w-2xl text-lg font-medium leading-8 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                Live POD titles now come directly from the publishing backend, including print cost, trim size, and ready-to-print files.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/books"
                  className="inline-flex items-center gap-2 rounded-2xl bg-orange-500 px-6 py-3.5 text-sm font-semibold text-white shadow-[0_18px_45px_-20px_rgba(249,115,22,0.8)] transition hover:-translate-y-0.5 hover:bg-orange-400"
                >
                  Browse all books
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href="#pod-books"
                  className={`rounded-2xl border px-6 py-3.5 text-sm font-semibold transition ${
                    isDark
                      ? 'border-white/10 bg-white/5 text-slate-200 hover:bg-white/10'
                      : 'border-slate-200 bg-white/85 text-slate-700 hover:bg-white'
                  }`}
                >
                  View POD collection
                </a>
              </div>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                <div
                  className={`rounded-[24px] border px-4 py-4 shadow-[0_20px_50px_-40px_rgba(249,115,22,0.45)] backdrop-blur ${
                    isDark ? 'border-white/10 bg-white/5' : 'border-orange-100 bg-white/78'
                  }`}
                >
                  <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>POD titles</p>
                  <p className={`mt-2 text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-950'}`}>{visiblePodBooks.length}</p>
                </div>
                <div
                  className={`rounded-[24px] border px-4 py-4 shadow-[0_20px_50px_-40px_rgba(249,115,22,0.45)] backdrop-blur ${
                    isDark ? 'border-white/10 bg-white/5' : 'border-orange-100 bg-white/78'
                  }`}
                >
                  <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Published</p>
                  <p className={`mt-2 text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-950'}`}>{publishedCount}</p>
                </div>
                <div
                  className={`rounded-[24px] border px-4 py-4 shadow-[0_20px_50px_-40px_rgba(249,115,22,0.45)] backdrop-blur ${
                    isDark ? 'border-white/10 bg-white/5' : 'border-orange-100 bg-white/78'
                  }`}
                >
                  <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Trim sizes</p>
                  <p className={`mt-2 text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-950'}`}>{formatCount}</p>
                </div>
              </div>

              {loadError ? <p className="mt-4 text-sm font-semibold text-orange-500">{loadError}</p> : null}
            </div>

            <div className="relative flex min-h-[320px] items-center justify-center sm:min-h-[360px]">
              <div className={`absolute h-72 w-72 rounded-full border ${isDark ? 'border-orange-400/25' : 'border-orange-200/45'}`} />
              <div className={`absolute h-[23rem] w-[23rem] rounded-full border ${isDark ? 'border-sky-300/20' : 'border-sky-200/45'}`} />
              <div className={`absolute h-64 w-64 rounded-full blur-3xl ${isDark ? 'bg-orange-500/18' : 'bg-orange-300/25'}`} />

              <div
                className={`relative flex h-[320px] w-full max-w-[470px] items-center justify-center rounded-[36px] border px-4 backdrop-blur-xl sm:h-[380px] ${
                  isDark
                    ? 'border-white/10 bg-white/5 shadow-[0_26px_70px_-50px_rgba(0,0,0,0.7)]'
                    : 'border-white/65 bg-white/32 shadow-[0_26px_70px_-50px_rgba(15,23,42,0.18)]'
                }`}
              >
                {featuredBooks.map((book, index) => {
                  const positions = [
                    'absolute left-1 top-20 -rotate-[13deg] sm:left-2 sm:top-24 sm:-rotate-[15deg]',
                    'absolute left-1/2 top-4 z-10 -translate-x-1/2 rotate-[-2deg] sm:top-6',
                    'absolute right-1 top-20 rotate-[13deg] sm:right-2 sm:top-24 sm:rotate-[15deg]',
                  ];

                  return (
                    <div
                      key={book.id}
                      className={`h-[205px] w-[126px] overflow-hidden rounded-[24px] border border-white/70 bg-[#130d0b] shadow-[0_28px_60px_-34px_rgba(15,23,42,0.35)] sm:h-[250px] sm:w-[165px] sm:rounded-[28px] ${positions[index] ?? positions[0]}`}
                      style={{ animation: `pod-showcase-float ${4.2 + index * 0.7}s ease-in-out infinite` }}
                    >
                      <SafeImage src={book.imageUrl} alt={book.title} className="h-full w-full object-cover" />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/55 to-transparent px-3 pb-3 pt-8 text-white sm:px-4 sm:pb-4 sm:pt-10">
                        <p className="text-[9px] font-semibold uppercase tracking-[0.22em] text-orange-100/75 sm:text-[10px]">Haapa POD</p>
                        <p className="mt-1.5 line-clamp-2 text-lg font-black leading-none sm:mt-2 sm:text-2xl">{book.title}</p>
                      </div>
                    </div>
                  );
                })}

                <div className="relative z-20 mt-28 grid h-24 w-24 place-items-center rounded-full border border-orange-200/60 bg-[radial-gradient(circle_at_30%_30%,#ffcf97_0%,#ff9a3c_45%,#d8660c_100%)] text-white shadow-[0_30px_90px_-35px_rgba(249,115,22,0.65)] sm:mt-36 sm:h-28 sm:w-28">
                  <div className="grid place-items-center rounded-full border border-white/30 bg-white/10 p-4 backdrop-blur sm:p-5">
                    <Printer className="h-8 w-8 sm:h-10 sm:w-10" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="pod-books" className="mx-auto mt-10 max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-orange-500">POD Collection</p>
              <h2 className={`mt-2 text-4xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Printable books we have right now</h2>
              <p className={`mt-3 max-w-3xl text-base font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                {isLoading ? 'Syncing POD catalog from the backend...' : 'Explore live POD-ready titles prepared for beautiful physical printing.'}
              </p>
            </div>
          </div>

          {isLoading && visiblePodBooks.length === 0 ? (
            <article className={`mt-7 rounded-[28px] border px-5 py-10 text-center ${isDark ? 'border-white/10 bg-white/5 text-slate-300' : 'border-orange-100 bg-white text-slate-600'}`}>
              Loading POD catalog...
            </article>
          ) : (
            <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {visiblePodBooks.map((book) => (
                <article
                  key={book.id}
                  className={`group overflow-hidden rounded-[24px] border transition hover:-translate-y-1 ${
                    isDark
                      ? 'border-white/10 bg-[#120d0b] shadow-[0_22px_70px_-52px_rgba(0,0,0,0.78)] hover:border-orange-400/40 hover:shadow-[0_30px_85px_-52px_rgba(249,115,22,0.32)]'
                      : 'border-orange-200 bg-white shadow-[0_22px_70px_-52px_rgba(249,115,22,0.48)] hover:shadow-[0_30px_85px_-52px_rgba(249,115,22,0.66)]'
                  }`}
                >
                  <div className="relative overflow-hidden">
                    <SafeImage
                      src={book.imageUrl}
                      alt={book.title}
                      className="h-60 w-full object-cover transition duration-500 group-hover:scale-[1.04]"
                    />
                    <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-[#17120f]/88 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-200 backdrop-blur">
                      <Printer className="h-3.5 w-3.5" />
                      {book.podStatus === 'published' ? 'Published' : 'POD Ready'}
                    </div>
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/42 to-transparent px-4 pb-3 pt-16 text-white">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-orange-100/85">{book.author}</p>
                      <h3 className="mt-1.5 line-clamp-2 text-xl font-semibold leading-tight">{book.title}</h3>
                    </div>
                  </div>

                  <div className="space-y-3 p-4">
                    <p className={`line-clamp-3 text-sm leading-6 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{book.description}</p>

                    <div className={`flex flex-wrap items-center gap-2 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {book.size ? (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-semibold ${isDark ? 'bg-orange-500/12 text-orange-200' : 'bg-orange-50 text-orange-700'}`}>
                          {book.size}
                        </span>
                      ) : null}
                      {book.pageCount ? <span>{book.pageCount} pages</span> : null}
                      {book.coverType ? <span>{book.coverType} cover</span> : null}
                    </div>

                    <div className="flex items-end justify-between gap-3">
                      <div className="space-y-1">
                        <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Print price</p>
                        {usdToRwfRate && usdToRwfRate > 0 ? (
                          <>
                            <p className={`mt-1 text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {formatUsd(convertRwfToUsd(book.printPrice, usdToRwfRate))}
                            </p>
                            <p className={`mt-1 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                              Pay in RWF: {formatRwf(book.printPrice)}
                            </p>
                          </>
                        ) : (
                          <p className={`mt-1 text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>RWF {book.printPrice.toLocaleString()}</p>
                        )}
                      </div>
                      <Link
                        to={`/pod/${book.id}`}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_14px_32px_-18px_rgba(249,115,22,0.75)] transition-all duration-150 hover:-translate-y-0.5 hover:bg-orange-450 sm:min-w-[130px]"
                      >
                        View book
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </section>
  );
};

export default PrintOnDemandPage;
